import React, { useState, useEffect, useRef } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import ClientTabs from "../Clients/ClientTabs";

const initialFieldValues = {
  paymentGatewayId: "00000000-0000-0000-0000-000000000000",
  companyId: "00000000-0000-0000-0000-000000000000",
  paymentGatewayName: "",
  devapiurl: "",
  devapisecret: "",
  devapikey: "",
  prodapiurl: "",
  prodapisecret: "",
  prodapikey: "",
  type: "DEVELOPMENT",
  transactionFee: 0,
};

const ClientPaymentGateway = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [addShow, setAddShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Filter states for main list
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Modal client search states
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalShowSuggestions, setModalShowSuggestions] = useState(false);
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [modalSelectedClient, setModalSelectedClient] = useState(null);

  // Filter refs
  const filtersRef = useRef({
    companyId: null,
    pageNumber: 1
  });

  // Generate suggestions based on search term for main list
  const generateSuggestions = (searchValue) => {
    if (!searchValue || !searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();

    const companyMatches = allClients
      .filter((client) => client.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 5);
    const brandMatches = allClients
      .filter((client) => client.brandName?.toLowerCase().includes(searchLower) && 
        !client.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 3);
    const emailMatches = allClients
      .filter((client) => client.email?.toLowerCase().includes(searchLower) && 
        !client.companyName?.toLowerCase().includes(searchLower) && 
        !client.brandName?.toLowerCase().includes(searchLower))
      .slice(0, 2);

    setSuggestions([...companyMatches, ...brandMatches, ...emailMatches]);
  };

  // Generate suggestions for modal client search
  const generateModalSuggestions = (searchValue) => {
    if (!searchValue || !searchValue.trim()) {
      setModalSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();

    const companyMatches = allClients
      .filter((client) => client.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 5);
    const brandMatches = allClients
      .filter((client) => client.brandName?.toLowerCase().includes(searchLower) && 
        !client.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 3);
    const emailMatches = allClients
      .filter((client) => client.email?.toLowerCase().includes(searchLower) && 
        !client.companyName?.toLowerCase().includes(searchLower) && 
        !client.brandName?.toLowerCase().includes(searchLower))
      .slice(0, 2);

    setModalSuggestions([...companyMatches, ...brandMatches, ...emailMatches]);
  };

  // Fetch all clients for dropdown suggestions
  const fetchAllClients = async () => {
    try {
      const resourceUrl = `${config.GETALLCOMPANIES}?companyType=CLIENT`;
      const response = await apiService.getAll(resourceUrl);
      const data = Array.isArray(response) ? response : (response?.data || []);
      setAllClients(data);
    } catch (error) {
      console.error("Failed to fetch clients for suggestions:", error);
    }
  };

  // Fetch payment gateways with company filter
  const getPaymentGateways = async (currentPage = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      const currentCompanyId = filtersRef.current.companyId;
      const currentPageNumber = currentPage || filtersRef.current.pageNumber;

      // Build URL with query parameters
      const params = new URLSearchParams();
      params.append('PageNumber', currentPageNumber);
      params.append('PageSize', pageSize);

      if (currentCompanyId) {
        params.append('companyId', currentCompanyId);
      }

      const url = `${config.GETALLPAYMENTGATEWAYS}?${params.toString()}`;
      
      const response = await apiService.getAll(url);
      
      // Handle different response formats
      const data = response?.data || response || [];
      const totalRecordsCount = response?.totalRecords || response?.totalCount || data.length || 0;
      const totalPagesCount = response?.totalPages || Math.ceil(totalRecordsCount / pageSize) || 1;

      setLists(Array.isArray(data) ? data : []);
      setTotalRecords(totalRecordsCount);
      setTotalPages(totalPagesCount);
      setPageNumber(response?.pageNumber || currentPageNumber);
    } catch (error) {
      console.error("Failed to fetch payment gateways:", error);
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  // Search by company name (main list filtering)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    generateSuggestions(value);
  };

  // Handle search button click - only call API if client is selected
  const handleSearchButtonClick = () => {
    if (selectedCompanyId) {
      // Client is selected, call API
      filtersRef.current.pageNumber = 1;
      setPageNumber(1);
      getPaymentGateways(1);
      setShowSuggestions(false);
      setSuggestions([]);
    } else {
      // No client selected, show alert
      alert('Please select a client from the suggestions first.');
    }
  };

  const handleSuggestionClick = (client) => {
    setSearchTerm(client.companyName);
    setSelectedCompanyId(client.companyId);
    setSelectedCompanyName(client.companyName);
    setSelectedClient(client);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
    setSelectedClient(null);
    filtersRef.current.companyId = null;
    setShowSuggestions(false);
    setSuggestions([]);
    filtersRef.current.pageNumber = 1;
    setPageNumber(1);
    getPaymentGateways(1);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowSuggestions(false);
      handleSearchButtonClick();
    }
  };

  // Modal client search handlers
  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    setModalShowSuggestions(true);
    
    if (!value.trim()) {
      setModalSuggestions([]);
      setModalSelectedClient(null);
      setValues({
        ...values,
        companyId: "00000000-0000-0000-0000-000000000000"
      });
      return;
    }

    generateModalSuggestions(value);
  };

  const handleModalSuggestionClick = (client) => {
    setModalSearchTerm(client.companyName);
    setModalSelectedClient(client);
    setModalShowSuggestions(false);
    setModalSuggestions([]);
    setValues({
      ...values,
      companyId: client.companyId
    });
    // Clear error if exists
    if (errors.companyId !== undefined) {
      setErrors({ ...errors, companyId: true });
    }
  };

  const handleModalInputBlur = () => {
    setTimeout(() => setModalShowSuggestions(false), 200);
  };

  const handleModalClearSearch = () => {
    setModalSearchTerm("");
    setModalSelectedClient(null);
    setModalShowSuggestions(false);
    setModalSuggestions([]);
    setValues({
      ...values,
      companyId: "00000000-0000-0000-0000-000000000000"
    });
  };

  // Pagination handlers
  const GetPageData = (number) => {
    if (number > 0 && number <= totalPages) {
      setPageNumber(number);
      filtersRef.current.pageNumber = number;
      if (pageNumber !== number) {
        getPaymentGateways(number);
      }
    }
  };

  const GetFirstPageData = () => GetPageData(1);
  const GetLastPageData = () => GetPageData(totalPages);

  // Modal handlers
  const handleAddClose = () => {
    setAddShow(false);
    setRecordForEdit(null);
    setModalSearchTerm("");
    setModalSelectedClient(null);
    setModalSuggestions([]);
    setModalShowSuggestions(false);
    setValues(initialFieldValues);
  };

  const handleAddShow = () => {
    setRecordForEdit(null);
    // Don't pre-populate the client when creating new
    setValues(initialFieldValues);
    setModalSearchTerm("");
    setModalSelectedClient(null);
    setModalSuggestions([]);
    setModalShowSuggestions(false);
    setAddShow(true);
  };

  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Edit handler
  const showEditDetails = (data) => {
    setRecordForEdit(data);
    // Find the client name for display
    const client = allClients.find(c => c.companyId === data.companyId);
    const clientName = client?.companyName || data.companyName || '';
    
    setValues({
      paymentGatewayId: data.paymentGatewayId,
      companyId: data.companyId,
      paymentGatewayName: data.paymentGatewayName || "",
      devapiurl: data.devapiurl || "",
      devapisecret: data.devapisecret || "",
      devapikey: data.devapikey || "",
      prodapiurl: data.prodapiurl || "",
      prodapisecret: data.prodapisecret || "",
      prodapikey: data.prodapikey || "",
      type: data.type || "DEVELOPMENT",
      transactionFee: data.transactionFee || 0,
    });
    
    // For edit mode, we show the client name as read-only
    setModalSearchTerm(clientName);
    setModalSelectedClient(client || null);
    setModalSuggestions([]);
    setModalShowSuggestions(false);
    setAddShow(true);
  };

  // Delete handler
  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETEPAYMENTGATEWAY, itemToDelete);
        await getPaymentGateways(pageNumber);
      } catch (error) {
        console.error("Failed to delete payment gateway:", error);
      }
      handleCloseDeleteModal();
    }
  };

  // Validation
  const validate = () => {
    let temp = {};
    temp.paymentGatewayName = values.paymentGatewayName === "" ? false : true;
    temp.companyId = values.companyId === "00000000-0000-0000-0000-000000000000" ? false : true;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      
      const companyId = values.companyId;

      if (!companyId || companyId === "00000000-0000-0000-0000-000000000000") {
        setErrors({ ...errors, companyId: false });
        setBtnLoading(false);
        return;
      }

      var formData = {
        paymentGatewayId: values.paymentGatewayId,
        paymentGatewayName: values.paymentGatewayName,
        devapikey: values.devapikey,
        devapisecret: values.devapisecret,
        devapiurl: values.devapiurl,
        prodapikey: values.prodapikey,
        prodapisecret: values.prodapisecret,
        prodapiurl: values.prodapiurl,
        type: values.type,
        transactionFee: parseFloat(values.transactionFee) || 0,
        companyId: companyId,
      };

      try {
        const isNew = formData.paymentGatewayId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATEPAYMENTGATEWAY, formData);
        } else {
          await apiService.update(config.UPDATEPAYMENTGATEWAY, formData);
        }
        
        // Refresh the list with current filter
        await getPaymentGateways(pageNumber);
        resetForm();
      } catch (error) {
        console.error("Failed to save payment gateway:", error);
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldsToUppercase = ["paymentGatewayName"];
    setValues({
      ...values,
      [name]: fieldsToUppercase.includes(name) ? value.toUpperCase() : value,
    });
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setModalSearchTerm("");
    setModalSelectedClient(null);
    setModalSuggestions([]);
    setModalShowSuggestions(false);
    setAddShow(false);
    setBtnLoading(false);
    setErrors({});
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " is-invalid" : "";

  // Initial load
  useEffect(() => {
    if (user && !authLoading) {
      fetchAllClients();
      getPaymentGateways(1);
    }
  }, [user, authLoading]);

  // Update filter ref when selectedCompanyId changes
  useEffect(() => {
    filtersRef.current.companyId = selectedCompanyId;
  }, [selectedCompanyId]);

  // Render pagination
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pageNumber - 2);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li className={`page-item ${pageNumber === i ? "active" : ""}`} key={i}>
          <Link className="page-link" onClick={() => GetPageData(i)}>{i}</Link>
        </li>
      );
    }
    return pageNumbers;
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <ClientTabs pageName="CLIENTPAYMENTGATEWAY" />
                </div>

                {/* Search and Filter Section */}
                <div className="card-body pb-0">
                  <div className="row align-items-end g-3">
                    <div className="col-md-7">
                      <label className="form-label">Search Client</label>
                      <div style={{ position: "relative" }}>
                        <div className="d-flex gap-2">
                          <div style={{ position: "relative", flex: 1 }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by company name, brand name, or email..."
                              value={searchTerm}
                              onChange={handleSearchChange}
                              onKeyPress={handleSearchKeyPress}
                              onFocus={() => {
                                if (searchTerm.trim()) {
                                  setShowSuggestions(true);
                                  generateSuggestions(searchTerm);
                                }
                              }}
                              onBlur={handleInputBlur}
                              style={{ paddingLeft: "40px", height: "45px" }}
                            />
                            <i 
                              className="ri-search-line" 
                              style={{ 
                                position: "absolute", 
                                left: "12px", 
                                top: "50%", 
                                transform: "translateY(-50%)", 
                                zIndex: 10, 
                                color: "#6c757d" 
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSearchButtonClick}
                            style={{ height: "45px", whiteSpace: "nowrap" }}
                          
                          >
                            <i className="ri-search-line me-1"></i> Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleClearSearch}
                            style={{ height: "45px", whiteSpace: "nowrap" }}
                          >
                            <i className="ri-refresh-line me-1"></i> Reset
                          </button>
                        </div>

                        {showSuggestions && suggestions.length > 0 && (
                          <div 
                            style={{ 
                              position: "absolute", 
                              width: "100%", 
                              marginTop: "4px", 
                              border: "1px solid #dee2e6", 
                              backgroundColor: "white", 
                              borderRadius: "6px", 
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)", 
                              zIndex: 1000,
                              maxHeight: "300px",
                              overflowY: "auto"
                            }}
                          >
                            {suggestions.map((client) => (
                              <div
                                key={client.companyId}
                                style={{ 
                                  padding: "10px 14px", 
                                  display: "flex", 
                                  alignItems: "center", 
                                  cursor: "pointer", 
                                  backgroundColor: "white",
                                  borderBottom: "1px solid #f0f0f0"
                                }}
                                onMouseEnter={(e) => { e.target.style.backgroundColor = "#f8f9fa"; }}
                                onMouseLeave={(e) => { e.target.style.backgroundColor = "white"; }}
                                onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(client); }}
                              >
                                <i className="ri-building-2-line me-3 text-secondary fs-5"></i>
                                <div>
                                  <div style={{ fontWeight: "600" }}>{client.companyName}</div>
                                  {client.brandName && client.brandName !== client.companyName && (
                                    <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                      Brand: {client.brandName}
                                    </div>
                                  )}
                                  {client.email && (
                                    <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                      {client.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
        
                    </div>

                    <div className="col-md-5 text-end">
                      <button
                        type="button"
                        className="btn btn-primary add-btn"
                        onClick={handleAddShow}
                      >
                        <i className="ri-add-line align-bottom me-1" /> Add Payment Gateway
                      </button>
                    </div>
                  </div>
                </div>

                {/* Table Section */}
                <div className="card-body">
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle" id="paymentGatewayTable">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th style={{ width: "60px" }}>#</th>
                          <th>Client</th>
                          <th>Payment Gateway</th>
                          <th>Type</th>
                          <th>Fee (%)</th>
                          <th style={{ width: "120px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {loading ? (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="mt-2 text-muted">Loading payment gateways...</p>
                            </td>
                          </tr>
                        ) : lists.length > 0 ? (
                          lists.map((v, index) => (
                            <tr key={v.paymentGatewayId}>
                              <td>{((pageNumber - 1) * pageSize) + index + 1}</td>
                              <td>
                                <div>
                                  <strong>{v.companyName || 'N/A'}</strong>
                                </div>
                              </td>
                              <td>
                                <span className="fw-semibold">{v.paymentGatewayName}</span>
                              </td>
                              <td>
                                <span className={`badge ${v.type === 'PRODUCTION' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                  {v.type || 'DEVELOPMENT'}
                                </span>
                              </td>
                              <td>{v.transactionFee || 0}%</td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Edit"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => showEditDetails(v)}
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted fs-5" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Delete"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) =>
                                        handleOpenDeleteModal(v.paymentGatewayId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted fs-5" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <i className="ri-inbox-line fs-1 d-block text-muted mb-2"></i>
                              {selectedCompanyId ? (
                                <p className="text-muted">No payment gateways found for this client.</p>
                              ) : (
                                <p className="text-muted">Please select a client to view their payment gateways.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 0 && (
                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                      <div className="flex-shrink-0">
                        <div className="text-muted">
                          Showing{" "}
                          <span className="fw-semibold">{lists.length}</span> of{" "}
                          <span className="fw-semibold">{totalRecords}</span>{" "}
                          Results
                        </div>
                      </div>
                      <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li
                          className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}
                          onClick={GetFirstPageData}
                        >
                          <Link className="page-link">Previous</Link>
                        </li>
                        {renderPageNumbers()}
                        <li
                          className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`}
                          onClick={GetLastPageData}
                        >
                          <Link className="page-link">Next</Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal with Searchable Client Dropdown */}
        <Modal show={addShow} onHide={handleAddClose} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {values.paymentGatewayId === "00000000-0000-0000-0000-000000000000"
                ? "Add"
                : "Edit"}{" "}
              Payment Gateway
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="row">
                {/* Client Search Field - For both Add and Edit */}
                {values.paymentGatewayId === "00000000-0000-0000-0000-000000000000" ? (
                  // Add mode - searchable dropdown
                  <div className="col-lg-12 mb-3">
                    <label htmlFor="clientSearch" className="form-label">
                      Search Client <span className="text-danger">*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <div className="input-group">
                        <input
                          type="text"
                          className={`form-control ${applyErrorClass("companyId")}`}
                          placeholder="Search by company name, brand name, or email..."
                          value={modalSearchTerm}
                          onChange={handleModalSearchChange}
                          onFocus={() => {
                            if (modalSearchTerm.trim()) {
                              setModalShowSuggestions(true);
                              generateModalSuggestions(modalSearchTerm);
                            }
                          }}
                          onBlur={handleModalInputBlur}
                        />
                        {modalSearchTerm && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleModalClearSearch}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                      </div>

                      {modalShowSuggestions && modalSuggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            width: "100%",
                            marginTop: "4px",
                            border: "1px solid #dee2e6",
                            backgroundColor: "white",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            zIndex: 1000,
                            maxHeight: "250px",
                            overflowY: "auto"
                          }}
                        >
                          {modalSuggestions.map((client) => (
                            <div
                              key={client.companyId}
                              style={{
                                padding: "10px 14px",
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                backgroundColor: "white",
                                borderBottom: "1px solid #f0f0f0"
                              }}
                              onMouseEnter={(e) => { e.target.style.backgroundColor = "#f8f9fa"; }}
                              onMouseLeave={(e) => { e.target.style.backgroundColor = "white"; }}
                              onMouseDown={(e) => { e.preventDefault(); handleModalSuggestionClick(client); }}
                            >
                              <i className="ri-building-2-line me-3 text-secondary fs-5"></i>
                              <div>
                                <div style={{ fontWeight: "600" }}>{client.companyName}</div>
                                {client.brandName && client.brandName !== client.companyName && (
                                  <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                    Brand: {client.brandName}
                                  </div>
                                )}
                                {client.email && (
                                  <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                                    {client.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {modalSelectedClient && (
                      <div className="mt-2">
                        <span className="badge bg-success">
                          <i className="ri-check-line me-1"></i>
                          Selected: {modalSelectedClient.companyName}
                        </span>
                      </div>
                    )}
                    {errors.companyId === false && (
                      <div className="invalid-feedback d-block">Please select a client</div>
                    )}
                  </div>
                ) : (
                  // Edit mode - show client name as read-only
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Client</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={modalSearchTerm || 'N/A'}
                      readOnly
                      disabled
                    />
                    <small className="text-muted">Client cannot be changed in edit mode</small>
                  </div>
                )}

                <div className="col-lg-4 mb-3">
                  <label htmlFor="paymentGatewayName" className="form-label">
                    Payment Gateway <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={45}
                    value={values.paymentGatewayName}
                    name="paymentGatewayName"
                    onChange={handleInputChange}
                    className={`form-control ${applyErrorClass("paymentGatewayName")}`}
                    placeholder="Enter Payment Gateway Name"
                  />
                  {errors.paymentGatewayName === false && (
                    <div className="invalid-feedback">Please enter payment gateway name</div>
                  )}
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="type" className="form-label">
                    Type
                  </label>
                  <select
                    name="type"
                    value={values.type}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="DEVELOPMENT">DEVELOPMENT</option>
                    <option value="PRODUCTION">PRODUCTION</option>
                  </select>
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="transactionFee" className="form-label">
                    Transaction Fee (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={values.transactionFee}
                    name="transactionFee"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter transaction fee"
                  />
                </div>

                {/* DEV API Fields */}
                <div className="col-12">
                  <h6 className="border-bottom pb-2 mb-3 text-primary">Development API Credentials</h6>
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="devapiurl" className="form-label">
                    DEV API URL
                  </label>
                  <input
                    type="text"
                    value={values.devapiurl}
                    name="devapiurl"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Dev API URL"
                  />
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="devapikey" className="form-label">
                    DEV API Key
                  </label>
                  <input
                    type="text"
                    value={values.devapikey}
                    name="devapikey"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Dev API Key"
                  />
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="devapisecret" className="form-label">
                    DEV API Secret
                  </label>
                  <input
                    type="text"
                    value={values.devapisecret}
                    name="devapisecret"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Dev API Secret"
                  />
                </div>

                {/* PROD API Fields */}
                <div className="col-12 mt-2">
                  <h6 className="border-bottom pb-2 mb-3 text-danger">Production API Credentials</h6>
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="prodapiurl" className="form-label">
                    PROD API URL
                  </label>
                  <input
                    type="text"
                    value={values.prodapiurl}
                    name="prodapiurl"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Prod API URL"
                  />
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="prodapikey" className="form-label">
                    PROD API Key
                  </label>
                  <input
                    type="text"
                    value={values.prodapikey}
                    name="prodapikey"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Prod API Key"
                  />
                </div>

                <div className="col-lg-4 mb-3">
                  <label htmlFor="prodapisecret" className="form-label">
                    PROD API Secret
                  </label>
                  <input
                    type="text"
                    value={values.prodapisecret}
                    name="prodapisecret"
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter Prod API Secret"
                  />
                </div>
              </div>

              <Modal.Footer>
                <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                  {btnLoading === false ? (
                    <button type="submit" className="btn btn-primary">
                      {values.paymentGatewayId === "00000000-0000-0000-0000-000000000000"
                        ? "Submit"
                        : "Update"}
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary" disabled>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </Modal.Footer>
            </form>
          </Modal.Body>
        </Modal>

        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default ClientPaymentGateway;